/**
 * Synthetic examples of the chronology routes (1.0b deliverables 19 and 20), set in "The Drowned
 * Lanterns" on the `p1b` entries of `EXAMPLE_IDS`: the Vesk Reckoning at version 1, Salt in the
 * Wells from 9 to 10 Ember 1042 AL, the undated Sunken Chapel after Bells Beneath the Marsh, The
 * Abbot's Bargain in the Unplaced tray, NOW at 14 Ember 1042 AL 20:00, Old Sef's Standing. The
 * calendar definition shown is the synthetic Vesk Almanac (`EXAMPLE_CALENDAR`). Two limits a screen
 * built on the mock must know, and override in its story where it shows dates: the ticks are
 * illustrative, not the engine's (NOW is not 32,542,804,800, 1.K-1's number for 14 Ember 1042 AL
 * 20:00 on the demo calendar), and every time point here carries the era `al` while the Vesk
 * Almanac's only era is `default`, so the engine would answer `invalid / unknown_key` for them
 * under `calendar.get`'s definition.
 */
import { EXAMPLE_CALENDAR } from '../calendar/example-calendar.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_ANSWER_IDS, EXAMPLE_NOW } from './examples.js';
const ids = EXAMPLE_IDS;
const answer = EXAMPLE_ANSWER_IDS;
const DAY = 86_400;
/** The tick of 1 Ember 1042 AL, 00:00 (illustrative). */
const EMBER_1 = 1_303_344_000;
/** An authored time point, materialized as the server stores it (the era key on every side). */
export function exampleTimePoint(loParts, precision, rawInput, hiParts = null) {
    return { loParts, hiParts, precision, qualifier: 'exact', label: '', rawInput };
}
const ember = (day, extra = {}) => ({
    era: 'al',
    year: 1042,
    month: 'ember',
    day,
    ...extra,
});
function dated(id, point, lo, hi) {
    return { id, point, ticks: { status: 'ok', lo, hi } };
}
// --------------------------------------------------------------------------------- calendar
export const EXAMPLE_CALENDAR_V1 = {
    id: ids.calendarVeskReckoning.id,
    name: EXAMPLE_CALENDAR.name,
    definition: EXAMPLE_CALENDAR,
    version: 1,
    note: null,
    sourceAssetId: null,
    updatedAt: EXAMPLE_NOW,
};
/**
 * The first commit (CS r1d, DECISIONS 98 item 11): `baseVersion: 0` while no calendar row exists
 * creates the calendar at version 1, named from its definition (1.L-1 deliverable 11).
 */
export const EXAMPLE_CALENDAR_FIRST_COMMIT = {
    request: { body: { baseVersion: 0, definition: EXAMPLE_CALENDAR, remap: {}, note: null } },
    response: EXAMPLE_CALENDAR_V1,
};
/** The draft moves no point but one: Salt in the Wells' start, a day later. */
export const EXAMPLE_CALENDAR_DIFF = {
    fromVersion: 1,
    toVersion: 2,
    toErrors: [],
    toWarnings: [{ code: 'leap_rule_dormant', severity: 'warning', path: '/leap' }],
    unchanged: 41,
    moved: [
        {
            id: answer.timePointSaltStart,
            before: { status: 'ok', lo: EMBER_1 + 8 * DAY, hi: EMBER_1 + 9 * DAY },
            after: { status: 'ok', lo: EMBER_1 + 9 * DAY, hi: EMBER_1 + 10 * DAY },
        },
    ],
    becameInvalid: [],
    becameValid: [],
    stillInvalid: [],
    episodesChanged: [],
};
/**
 * A Foundry calendar file, the synthetic Vesk Almanac as Foundry writes it: `EXAMPLE_CALENDAR`
 * is this file through P0-13's import rules with season base 0.
 */
export const EXAMPLE_FOUNDRY_CALENDAR = {
    name: EXAMPLE_CALENDAR.name,
    id: 'vesk-almanac',
    system: 'dnd5e',
    description: EXAMPLE_CALENDAR.description,
    years: { yearZero: 1000, firstWeekday: 4, leapYear: { leapStart: 1004, leapInterval: 4 } },
    months: {
        values: EXAMPLE_CALENDAR.months.map(({ name, abbreviation, ordinal, days }) => ({
            name,
            abbreviation,
            ordinal,
            days,
            startingWeekday: 4,
        })),
    },
    days: {
        values: EXAMPLE_CALENDAR.weekdays.map(({ name, abbreviation, ordinal }) => ({
            name,
            abbreviation,
            ordinal,
        })),
        daysPerYear: 360,
        hoursPerDay: 24,
        minutesPerHour: 60,
        secondsPerMinute: 60,
    },
    seasons: {
        values: EXAMPLE_CALENDAR.seasons.map(({ name, startDay, foundry }, i, all) => ({
            name,
            monthStart: foundry.monthStart,
            monthEnd: foundry.monthEnd,
            dayStart: startDay,
            dayEnd: all[(i + 1) % all.length]?.startDay,
        })),
    },
    moons: {
        values: EXAMPLE_CALENDAR.moons.map(({ name, cycleLength, offset }) => ({
            name,
            cycleLength,
            offset,
        })),
    },
};
export const EXAMPLE_CALENDAR_IMPORT = {
    definition: EXAMPLE_CALENDAR,
    warnings: [
        { code: 'leap_rule_dormant', severity: 'warning' },
        { code: 'season_bounds_touch', severity: 'warning', seasonIndex: 0 },
        { code: 'season_bounds_touch', severity: 'warning', seasonIndex: 1 },
        { code: 'season_bounds_touch', severity: 'warning', seasonIndex: 2 },
        { code: 'season_bounds_touch', severity: 'warning', seasonIndex: 3 },
    ],
};
/** `calendar.exportFoundry`'s file: the keys Foundry reads, each season ending a day early. */
export const EXAMPLE_FOUNDRY_EXPORT = {
    contentType: 'application/json',
    body: JSON.stringify({
        ...EXAMPLE_FOUNDRY_CALENDAR,
        id: undefined,
        system: undefined,
        moons: undefined,
        seasons: {
            values: EXAMPLE_FOUNDRY_CALENDAR.seasons.values.map((s) => ({
                ...s,
                dayEnd: ((s.dayEnd ?? 0) + 359) % 360,
            })),
        },
    }),
};
/** `chrono.preview` of "12 Ember 1042" for a quest: a day, exact. */
export const EXAMPLE_WHEN_PREVIEW = {
    kind: 'point',
    canonical: '12 Ember 1042',
    precision: 'day',
    qualifier: 'exact',
    status: 'ok',
    reason: null,
    earliest: EMBER_1 + 11 * DAY,
    latest: EMBER_1 + 12 * DAY,
    anchorChoice: null,
};
// ------------------------------------------------------------------- placements, constraints
function placement(fields) {
    return {
        rv: 1,
        anchorBlockId: null,
        role: 'occurs',
        fieldDefId: null,
        label: null,
        summary: null,
        start: null,
        end: null,
        ongoing: false,
        status: 'canon',
        sessionId: null,
        note: null,
        origin: 'manual',
        effectiveStatus: 'canon',
        span: { empty: false, lo: null, hi: null },
        boundLo: null,
        boundHi: null,
        chronRank: null,
        participants: [],
        constraints: [],
        createdAt: EXAMPLE_NOW,
        updatedAt: EXAMPLE_NOW,
        ...fields,
    };
}
/** The Sunken Chapel after Bells Beneath the Marsh (Bells' end before the Chapel's start). */
export const EXAMPLE_CONSTRAINT = {
    id: ids.constraintSunkenChapelAfterBells.id,
    rv: 1,
    beforeId: answer.placementBells,
    beforeEdge: 'end',
    afterId: ids.placementSunkenChapel.id,
    afterEdge: 'start',
    relation: 'before',
    minLagN: null,
    maxLagN: null,
    lagUnit: null,
    note: null,
    origin: 'manual',
    status: 'active',
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** Salt in the Wells, from 9 to 10 Ember 1042 AL. */
export const EXAMPLE_PLACEMENT = placement({
    id: ids.placementSaltInTheWells.id,
    documentId: ids.questSaltInTheWells.id,
    kind: 'span',
    orderKey: 'a1',
    start: dated(answer.timePointSaltStart, exampleTimePoint(ember(9), 'day', '9 Ember 1042'), EMBER_1 + 8 * DAY, EMBER_1 + 9 * DAY),
    end: dated(answer.timePointSaltEnd, exampleTimePoint(ember(10), 'day', '10 Ember 1042'), EMBER_1 + 9 * DAY, EMBER_1 + 10 * DAY),
    span: { empty: false, lo: EMBER_1 + 8 * DAY, hi: EMBER_1 + 10 * DAY },
    boundLo: EMBER_1 + 8 * DAY,
    boundHi: EMBER_1 + 10 * DAY,
    chronRank: 2048,
    participants: [{ docId: ids.npcOrlaVenn.id, role: '' }],
});
/** The placement `placements.create`'s example makes: Bells Beneath the Marsh on 1 Mist 1042 AL. */
export const EXAMPLE_PLACEMENT_CREATED = placement({
    id: answer.placementCreated,
    documentId: ids.questBellsBeneathTheMarsh.id,
    kind: 'moment',
    orderKey: 'a2',
    start: dated(answer.timePointCreated, exampleTimePoint({ era: 'al', year: 1042, month: 'mist', day: 1 }, 'day', '1 Mist 1042'), EMBER_1 + 36 * DAY, EMBER_1 + 37 * DAY),
    span: { empty: false, lo: EMBER_1 + 36 * DAY, hi: EMBER_1 + 37 * DAY },
    boundLo: EMBER_1 + 36 * DAY,
    boundHi: EMBER_1 + 37 * DAY,
    chronRank: 3072,
    participants: [{ docId: ids.npcPaleAbbot.id, role: 'host' }],
});
/**
 * `placements.list` with `participating=true` (CS r2c, item W1): the live placements of other
 * documents that name the path document as a participant. For Captain Orla Venn, Bells Beneath
 * the Marsh's placement, where she witnesses (distinct from the route's own example). The mock answers it for any `participating=true`; the
 * route's own example (no query) is unchanged.
 */
export const EXAMPLE_PLACEMENTS_PARTICIPATING = {
    items: [
        {
            ...EXAMPLE_PLACEMENT_CREATED,
            participants: [{ docId: ids.npcOrlaVenn.id, role: 'witness' }],
        },
    ],
    next: null,
};
/** The constraint `constraints.create`'s example makes: the Chapel before the Bargain. */
export const EXAMPLE_CONSTRAINT_CREATED = {
    ...EXAMPLE_CONSTRAINT,
    id: answer.constraintCreated,
    beforeId: ids.placementSunkenChapel.id,
    afterId: ids.placementAbbotsBargain.id,
};
const docRef = (id, fid, title, typeKey) => ({
    id,
    fid,
    title,
    titlePending: false,
    typeKey,
    library: 'canon',
    isStub: false,
});
export const EXAMPLE_CHRONICLE_ROW = {
    placement: EXAMPLE_PLACEMENT,
    document: docRef(ids.questSaltInTheWells.id, 'SaltInTheWells01', 'Salt in the Wells', 'quest'),
    violations: [],
};
/** The Unplaced tray: The Abbot's Bargain, undated and unconstrained. */
export const EXAMPLE_UNPLACED_ROW = {
    placement: placement({
        id: ids.placementAbbotsBargain.id,
        documentId: ids.questAbbotsBargain.id,
        kind: 'undated',
        orderKey: 'a0',
    }),
    document: docRef(ids.questAbbotsBargain.id, 'AbbotsBargain001', "The Abbot's Bargain", 'quest'),
    violations: [],
};
// ---------------------------------------------------------------------------------- NOW
const NOW_TICK = EMBER_1 + 13 * DAY + 20 * 3600;
/** NOW at 14 Ember 1042 AL, 20:00, with no advance. */
export const EXAMPLE_CLOCK = {
    rv: 1,
    origin: dated(answer.timePointClockOrigin, exampleTimePoint(ember(14, { hour: 20, minute: 0 }), 'minute', '14 Ember 1042, 20:00'), NOW_TICK, NOW_TICK + 60),
    presentTick: NOW_TICK,
    presentParts: ember(14, { hour: 20, minute: 0 }),
    advances: [],
};
/**
 * The first NOW (CS r2b item L3): `baseRv: 0` while no clock exists, a `set` to 14 Ember 1042 AL
 * 20:00, creates the clock at rv 1 with that point as its origin and no advance: `EXAMPLE_CLOCK`.
 */
export const EXAMPLE_CLOCK_FIRST_SET = {
    request: {
        body: {
            kind: 'set',
            set: EXAMPLE_CLOCK.origin.point,
            reason: 'bootstrap',
            baseRv: 0,
        },
    },
    response: EXAMPLE_CLOCK,
};
/** NOW after `clock.advance`'s example: a long rest of eight hours in session 3. */
export const EXAMPLE_CLOCK_ADVANCED = {
    ...EXAMPLE_CLOCK,
    rv: 2,
    presentTick: NOW_TICK + 8 * 3600,
    presentParts: ember(15, { hour: 4, minute: 0 }),
    advances: [
        {
            id: answer.clockAdvanceCreated,
            sessionId: ids.sessionSunkenChapel.id,
            captureId: null,
            orderKey: 'a0',
            kind: 'delta',
            deltaN: 8,
            deltaUnit: 'hour',
            set: null,
            reason: 'long_rest',
            note: null,
            undoneAt: null,
            origin: 'manual',
            fromTick: NOW_TICK,
            toTick: NOW_TICK + 8 * 3600,
            createdAt: EXAMPLE_NOW,
        },
    ],
};
// --------------------------------------------------------------------------------- states
/** Old Sef's Standing: "Wary" from 9 Ember 1042 AL, with no end. */
export const EXAMPLE_STATE = {
    id: ids.stateOldSefStanding.id,
    rv: 1,
    documentId: ids.npcOldSef.id,
    fieldDefId: ids.fieldNpcStanding.id,
    value: 'Wary',
    from: { timePoint: exampleTimePoint(ember(9), 'day', '9 Ember 1042') },
    to: null,
    sessionId: null,
    note: null,
    origin: 'manual',
    certainSpan: { empty: false, lo: EMBER_1 + 9 * DAY, hi: null },
    possibleSpan: { empty: false, lo: EMBER_1 + 8 * DAY, hi: null },
    createdAt: EXAMPLE_NOW,
    updatedAt: EXAMPLE_NOW,
};
/** The state `states.create`'s example makes: "Ally" from 1 Mist 1042 AL. */
export const EXAMPLE_STATE_CREATED = {
    ...EXAMPLE_STATE,
    id: answer.stateCreated,
    value: 'Ally',
    from: {
        timePoint: exampleTimePoint({ era: 'al', year: 1042, month: 'mist', day: 1 }, 'day', '1 Mist 1042'),
    },
    certainSpan: { empty: false, lo: EMBER_1 + 37 * DAY, hi: null },
    possibleSpan: { empty: false, lo: EMBER_1 + 36 * DAY, hi: null },
};
//# sourceMappingURL=examples-chronology.js.map